export class CedeApi {
  // eslint-disable-next-line class-methods-use-this
  url() {
    return 'https://cede.store'
  }

  async checkInstall() {
    const provider = await this.provider()
    if (!provider) {
      return false
    }
    return true
  }

  async checkUnlocked() {
    const provider = await this.provider()
    return provider.getIsUnlocked()
  }

  async unlock(first: boolean) {
    const provider = await this.provider()
    if (!provider.getIsUnlocked()) {
      await provider.request({ method: 'connect', params: { onboard: true, silent: first, version: 1 } })
      return provider
    }
    return provider
  }

  async connect() {
    if (!(await this.checkInstall())) {
      throw new Error('Please install the plugin')
    }
    const provider = await this.provider()
    if (!provider.getIsUnlocked()) {
      throw new Error('Please unlock the plugin')
    }
    return provider
  }

  async getExchanges(): Promise<
    {
      name: string
      id: string
      logo: string
    }[]
  > {
    const provider = await this.connect()
    const exchange = (await provider.request({ method: 'supportedExchanges', params: { version: 1 } })).map((it) => {
      return {
        name: it.name,
        id: it.id,
        logo: it.logo,
      }
    })
    const res = []
    for (const item of exchange) {
      // eslint-disable-next-line no-await-in-loop
      const tokens = await this.getTokens(item.id)
      if (tokens.length > 0) {
        res.push(item)
      }
    }
    return res
  }

  async getTokens(exchangeId: string): Promise<
    {
      id: string
      name: string
      logo: string
      balance: string
      price: string
    }[]
  > {
    const provider = await this.connect()
    const account = await this.getAccount(exchangeId)
    if (!account) {
      return []
    }
    const prices = await this.getPrice()
    const tokens = []
    const tokenBalances = await provider.request({
      method: 'withdrawableTokens',
      params: {
        accountId: account,
        network: undefined,
        version: 1,
      },
    })
    // eslint-disable-next-line guard-for-in
    for (const symbol in tokenBalances) {
      tokens.push({
        price: prices[symbol],
        id: symbol,
        name: symbol,
        logo: `https://cdn.cedelabs.io/token/${symbol.toLowerCase()}.svg`,
        balance: tokenBalances[symbol].freeBalance,
      })
    }
    return tokens
  }

  async getAccount(exchangeId: string) {
    const provider = await this.connect()
    const accounts = (await provider.request({ method: 'vaults', params: { version: 1 } }))
      .flatMap((it) => it.accounts)
      .filter((it) => it.exchangeId === exchangeId)
      .map((it) => {
        return it
      })
    if (accounts.length === 0) {
      return undefined
    }
    return accounts[0].id
  }

  async getChains(
    exchangeId: string,
    token: string,
  ): Promise<
    {
      name: string
      id: string
      chainId: number
      logo: string
      minAmount: string
    }[]
  > {
    const provider = await this.connect()
    const account = await this.getAccount(exchangeId)
    if (!account) {
      return []
    }

    const chains = []
    const networks = await provider.request({
      method: 'getNetworks',
      params: {
        accountId: account,
        tokenSymbol: token,
        opts: {
          toWithdraw: true,
        },
        version: 1,
      },
    })
    for (const network of networks) {
      chains.push({
        id: network.network,
        chainId: network.chainId,
        name: network.name,
        logo: `https://cdn.cedelabs.io/network/${network.network.toLowerCase()}.png`,
        minAmount: network.withdrawMin || '0',
      })
    }
    return chains
  }

  async getPrice() {
    const provider = await this.connect()
    return provider.request({ method: 'getPrices', params: { version: 1 } })
  }

  // eslint-disable-next-line class-methods-use-this
  async provider() {
    return (window as any).cede
  }

  async withdraw(exchangeId: string, token: string, chain: string, amount: string, address: string): Promise<void> {
    const provider = await this.connect()
    const account = await this.getAccount(exchangeId)
    if (!account) {
      throw new Error('No account found')
    }
    await provider.request({
      method: 'withdrawToDefi',
      params: {
        accountId: account,
        tokenSymbol: token,
        network: chain,
        amount,
        address,
        version: 1,
      },
    })
  }
}

export const cedeApi = new CedeApi()
